import { motion } from "framer-motion";
import { Leaf } from "lucide-react";

interface GlobalLoaderProps {
  /** Optional label shown discreetly under the leaf */
  label?: string;
  /** Use a transparent background to overlay on top of existing content */
  transparent?: boolean;
  /** Render fixed/full-screen (true) or inline within parent (false) */
  fullScreen?: boolean;
}

/**
 * GlobalLoader
 * - Centered "Leaf" mark (same icon used on the brand)
 * - Soft, continuous breathing animation (no harsh spin)
 * - Clean background, focus on the central element
 * - Lightweight: pure CSS/Framer transforms
 */
export default function GlobalLoader({
  label,
  transparent = false,
  fullScreen = true,
}: GlobalLoaderProps) {
  const containerClasses = [
    fullScreen ? "fixed inset-0 z-[9999]" : "w-full h-full",
    "flex flex-col items-center justify-center",
    transparent ? "bg-transparent" : "bg-background",
  ].join(" ");

  return (
    <div
      className={containerClasses}
      role="status"
      aria-live="polite"
      aria-label={label || "Carregando"}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="relative flex items-center justify-center"
      >
        {/* Soft halo */}
        <motion.div
          aria-hidden
          className="absolute inset-0 rounded-full bg-primary/10 blur-xl"
          animate={{ scale: [1, 1.25, 1], opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        />
        {/* Leaf mark — gentle breathing + subtle sway */}
        <motion.div
          animate={{
            scale: [1, 1.08, 1],
            rotate: [-4, 4, -4],
          }}
          transition={{
            duration: 2.4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="relative"
        >
          <Leaf className="h-12 w-12 text-primary drop-shadow-sm" strokeWidth={1.75} />
        </motion.div>
      </motion.div>

      {label && (
        <motion.p
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 0.7, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="mt-4 text-sm text-muted-foreground font-body"
        >
          {label}
        </motion.p>
      )}
    </div>
  );
}
